import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Megaphone, MessagesSquare, Sparkles } from 'lucide-react';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { useWhoami } from '@cotera/client/app/stores/org';
import {
  PageBarSidebarTrigger,
  PageNavStrip,
} from '@cotera/client/v0/components/ui/page-bar-sidebar-trigger';
import { consumeSignupPendingChatConversationId } from '../models/pending-chat-submission';
import { NewChatPromptInput } from '../components/new-chat-prompt-input';
import type { NewChatPromptInputHandle } from '../components/new-chat-prompt-input';

const SUB_TITLES = [
  "What's on your mind?",
  'What are you working on?',
  'Ready to build something?',
  'What brings you here today?',
  'What would you like to create?',
  'What can I help with?',
  "Let's get started",
  'What are you thinking about?',
  'What would you like to build?',
  'What can I help you automate?',
  'What would you like to explore?',
];

const PLACEHOLDERS = [
  'Ask a question, start a project, or explore...',
  "Describe what you're building or what you need...",
  'Tell me what you have in mind...',
  'Start with a question, idea, or task...',
  "Share what you're thinking about...",
  'Describe your idea or ask me anything...',
  'What would you like to build or explore?',
  'Type anything to get started...',
];

// Greetings that include the user's name
const GREETINGS_WITH_NAME = [
  "Hey, I'm Coco!",
  "Hi, I'm Coco!",
  'Welcome, {name}!',
  'Hey there, {name}!',
  'Hi, {name}!',
];

// Greetings when no name is available
const GREETINGS_NO_NAME = [
  "Hey, I'm Coco!",
  "Hi, I'm Coco!",
  'Hey!',
  'Hi!',
  'Welcome!',
];

const getTimeEmoji = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) {
    return '☀️';
  }
  if (hour < 18) {
    return '😊';
  }
  return '🌙';
};

const WelcomeHeader: React.FC = () => {
  const { user } = useAuth();
  const firstName = user?.firstName;

  const emoji = useMemo(() => getTimeEmoji(), []);
  const greeting = useMemo(() => {
    const greetings = firstName ? GREETINGS_WITH_NAME : GREETINGS_NO_NAME;
    const template = greetings[Math.floor(Math.random() * greetings.length)]!;
    return firstName ? template.replace('{name}', firstName) : template;
  }, [firstName]);
  const subtitle = useMemo(
    () => SUB_TITLES[Math.floor(Math.random() * SUB_TITLES.length)],
    []
  );

  return (
    <div className="text-center shrink-0">
      {/* Steps down rather than holding 48px: at that size a greeting carrying
          a name ("Hey there, Christopher!") took three lines on a 375px screen
          and pushed the composer — the one thing this page exists for — below
          the fold. `text-balance` keeps whatever wrapping is left even. */}
      <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-foreground mb-3 sm:mb-4 font-semibold text-balance">
        {greeting} {emoji}
      </h1>
      <p className="text-lg sm:text-xl text-slate-500 text-balance">
        {subtitle}
      </p>
    </div>
  );
};

const USE_CASE_CATEGORIES: {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  label: string;
  prompts: { title: string; detail: string }[];
}[] = [
  {
    icon: BarChart3,
    label: 'Sales',
    prompts: [
      {
        title: 'Pre-meeting research',
        detail:
          "Given a person's name and company, use google/search to find their LinkedIn profile URL. Then use linkedin/profile/getWithPosts to pull detailed profile data including current role, tenure, career history, and recent posts. Use apollo/enrichment/people to get contact details. Look for hooks like a new role, recent content they published, or shared background. Give me a quick bio, what they care about based on their posts, talking points I can reference, and a suggested personalized opener for outreach.",
      },
      {
        title: 'Find decision maker',
        detail:
          "Research a company and find the key decision maker for a specific team or department. Use google/search to understand what the company does and how big they are. Use apollo/search-people to find senior titles — VP, Director, Head of, C-level — in the target department, filtering for people currently employed with 6+ months tenure. Use linkedin/profile/getWithPosts to build a complete profile on the best match. Give me their name, title, LinkedIn URL, career background, and why they're the right contact. Include 1-2 backup options if available.",
      },
      {
        title: 'Leadership priorities finder',
        detail:
          "Given a company name, use google/search to find the company's website domain. Use apollo/search-people with the company domain to find their leadership team — C-suite, VPs, and founders. For key executives, use google/search to find recent YouTube podcast appearances, interviews, or talks from the last 12 months. Use youtubeTranscript/transcript/getTranscript to extract strategic priorities, challenges mentioned, and future plans. Include direct quotes where possible. Summarize what this means if I'm trying to sell to or partner with them.",
      },
      {
        title: 'Persona enrichment',
        detail:
          "Enrich a lead with full profile and company data. I'll give you a name and company (or email, or LinkedIn URL). If no LinkedIn URL is provided, use google/search to find their LinkedIn profile. Use apollo/enrichment/people to pull full contact data including email and phone. Use linkedin/profile/getWithPosts to get their current role, tenure, career history, and recent activity. Use linkedin/insights/get to get company size, industry, funding, and growth signals. Flag any notable signals like a new role or company expansion.",
      },
    ],
  },
  {
    icon: Megaphone,
    label: 'Marketing',
    prompts: [
      {
        title: 'Competitor keyword research',
        detail:
          "Find what keywords my competitors rank for. Use google/search to find competitor pages that rank well. Use similarweb/traffic/getTrafficData to get their top organic keywords. Use google-keyword/top-keys to get search volume and difficulty scores. Identify keyword gaps where they rank but I don't. Look for quick wins — keywords with volume > 500 and difficulty < 50. Group results by intent (informational, commercial, transactional) and recommend which to prioritize.",
      },
      {
        title: 'Social listening',
        detail:
          'Monitor mentions of my brand across social channels. Use google/search to find recent discussions. Use twitter/posts/get to check Twitter mentions. Use reddit/posts/extractPost to pull Reddit threads. Use newsapi/search to find news coverage. Flag high-priority items like negative viral content, press coverage, or influencer mentions. Compile a digest grouped by source with sentiment, engagement counts, and notable quotes. Send the summary using slack/messaging/sendMessage.',
      },
      {
        title: 'Buyer persona from reviews',
        detail:
          'Analyze online reviews for my company. Use trustpilot/reviews/getReviews to pull Trustpilot reviews. Use google-maps/reviews/getPlaceReviews to pull Google Maps reviews. Identify what customers praise, what pain points led them to us, and what alternatives they considered. Build a buyer persona based on the evidence — demographics, values, decision factors, and the language they use. Include real quotes from reviews and marketing implications for how to reach this audience.',
      },
      {
        title: 'Competitor ads research',
        detail:
          "Spy on competitor ads across Google, Meta, and other platforms. Use google/search to find competitor ad examples, landing pages, and ad transparency pages. Use search-api/google-ads-search to pull ads from Google Ads Transparency Center. Use search-api/meta-ads-search to find Facebook and Instagram ads from the Meta Ad Library. Use similarweb/traffic/getTrafficData to analyze paid traffic share and top paid keywords. Give me recommendations for my own ad campaigns based on what's working for them.",
      },
    ],
  },
  {
    icon: MessagesSquare,
    label: 'Support',
    prompts: [
      {
        title: 'Account monitoring',
        detail:
          'Monitor my customer accounts for news, social mentions, and significant events. Use google/search and newsapi/search to find funding announcements, leadership changes, layoffs, product launches, and negative press. Use twitter/posts/get to check for social activity. Prioritize risk signals like champion departures or acquisition news as high priority, expansion signals like funding rounds as medium, and general press mentions as low. Summarize findings per account with what happened, why it matters, and a recommended next step. Send relevant alerts via slack/messaging/sendMessage.',
      },
      {
        title: 'Social listening',
        detail:
          'Monitor mentions of my brand across social channels. Use google/search to find recent discussions. Use twitter/posts/get to check Twitter mentions. Use reddit/posts/extractPost to pull Reddit threads. Use newsapi/search to find news coverage. Flag high-priority items like negative viral content, press coverage, or influencer mentions. Compile a digest grouped by source with sentiment, engagement counts, and notable quotes. Send the summary using slack/messaging/sendMessage.',
      },
      {
        title: 'Voice of customer analysis',
        detail:
          'Analyze online reviews for a competitor to find positioning opportunities. Use google/search to find their Trustpilot page. Use trustpilot/reviews/getReviews to pull reviews, focusing on the last 12 months. Categorize repeated complaints and praise into themes. For each pain point, include a real customer quote and suggest how we could position against it. Identify what keeps their customers loyal and where their product falls short. Give me 2-3 specific messaging angles based on their weaknesses.',
      },
      {
        title: 'Support QA audit',
        detail:
          'Review a sample of recent support conversations and evaluate quality. Score each on response time, tone, accuracy, resolution completeness, and whether the agent followed up appropriately. Flag conversations where the customer had to repeat themselves, was escalated unnecessarily, or left without a clear resolution. Summarize common quality gaps across the team and recommend specific coaching areas or process improvements.',
      },
    ],
  },
  {
    icon: Sparkles,
    label: "Coco's pick",
    prompts: [
      {
        title: 'Be my hype person',
        detail:
          "Look me up on Google and LinkedIn — find my recent accomplishments, posts, career moves, anything public. Then gas me up about it. Tell me what I'm doing well, what's impressive about my trajectory, and why I should feel good about where I am. Be genuine but enthusiastic. I want to walk away from this feeling like I just got the world's best pep talk.",
      },
      {
        title: 'Write my out-of-office message',
        detail:
          'Write me an out-of-office email reply that\'s actually funny and memorable — not the usual "I\'m currently out of the office with limited access to email" snooze. Make it reflect some personality. Give me a few options ranging from mildly witty to unhinged. Keep them short enough that people actually read the whole thing.',
      },
      {
        title: 'Roast my competitors',
        detail:
          "Pick one of my competitors and do a deep dive on what people actually say about them online. Find their worst reviews, their most embarrassing social media moments, and the things their own customers complain about. Be brutally honest but make it fun — I want to know where they're dropping the ball so I can pick it up. Bonus points for screenshots-worthy quotes.",
      },
    ],
  },
];

const UseCasePills: React.FC<{
  onSelect: (prompt: string) => void;
}> = ({ onSelect }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handlePillClick = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  return (
    <div className="mt-4 flex flex-col items-center gap-2">
      <div className="flex flex-wrap justify-center gap-2">
        {USE_CASE_CATEGORIES.map((cat, i) => (
          <button
            key={cat.label}
            onClick={() => handlePillClick(i)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm cursor-pointer
              border transition-all duration-200 animate-in fade-in slide-in-from-left-2 fill-mode-both
              ${
                openIndex === i
                  ? 'bg-primary/10 border-primary/30 text-primary'
                  : 'bg-card/60 border-border/50 text-muted-foreground hover:bg-card hover:border-border'
              }
            `}
            style={{
              animationDelay: `${400 + i * 75}ms`,
              animationDuration: '300ms',
            }}
          >
            <cat.icon className="h-3.5 w-3.5" />
            {cat.label}
          </button>
        ))}
      </div>
      {openIndex !== null && (
        <div className="flex flex-wrap justify-center gap-2 mt-1 animate-in fade-in slide-in-from-top-1 duration-200">
          {USE_CASE_CATEGORIES[openIndex]!.prompts.map((prompt) => (
            <button
              key={prompt.title}
              onClick={() => onSelect(prompt.detail)}
              className="px-3 py-1.5 rounded-full text-sm cursor-pointer
                bg-card/40 border border-border/30 text-muted-foreground
                hover:bg-card hover:border-border hover:text-foreground
                transition-all duration-150"
            >
              {prompt.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Component for the welcome/new chat view - shows input and navigates on submit
export const CocoChatNew: React.FC = () => {
  const navigate = useNavigate();
  const org = useWhoami((s) => s.org);
  const inputRef = useRef<NewChatPromptInputHandle>(null);

  useTracking(CocoEvent.NewChatViewed, { orgId: org.id });

  useEffect(() => {
    const conversationId = consumeSignupPendingChatConversationId();
    if (conversationId === null) {
      return;
    }
    navigate(`/${org.slug}/chats/${conversationId}`);
  }, [navigate, org.slug]);

  const placeholder = useMemo(
    () => PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)],
    []
  );

  const handleUseCaseSelect = useCallback((prompt: string) => {
    inputRef.current?.setValue(prompt);
  }, []);

  return (
    <div className="flex h-full w-full flex-col min-h-0 overflow-hidden">
      {/* `PageBarSidebarTrigger`, not a bare `SidebarTrigger`: the app shell
          draws a nav strip for any page that turns out to have none, and it
          learns which pages those are from this component. A raw trigger is
          invisible to it, so this page had its own hamburger *and* the shell's
          stacked above it. */}
      <PageNavStrip>
        <PageBarSidebarTrigger />
      </PageNavStrip>
      <div className="v1-content-panel overflow-y-auto">
        {/* `px-4` on a phone, not `px-8`: 32px of gutter on each side of a
            375px screen left the composer 311px wide, and the pills beside it
            wrapped one word per line. */}
        {/* No `min-h-0` here, deliberately. `justify-center` centres the column
            in whatever height this box has, and with `min-h-0` that height was
            pinned to the panel's — so on a short screen the greeting and the
            composer overflowed *both* edges of a box that could not grow, and
            the scroll container above had nothing to scroll. Letting it grow
            past the panel means it centres while there is room and scrolls once
            there isn't. */}
        <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center px-4 py-8 sm:px-8 sm:py-10 md:px-10">
          <div className="relative">
            <div className="mb-6 sm:mb-8">
              <WelcomeHeader />
            </div>
            <NewChatPromptInput
              ref={inputRef}
              placeholder={placeholder}
              className="divide-y-0 pb-4 [&_[data-slot=input-group]]:rounded-3xl"
            />
            <UseCasePills onSelect={handleUseCaseSelect} />
          </div>
        </main>
      </div>
    </div>
  );
};
